import React, { useEffect, useRef, useState } from 'react';
import { authService } from '../../services/authService';
import { logUiEvent } from '../../services/uiLogger';
import { STAFF_HOST, STAFF_URL } from '../../lib/host';
import { emailLayoutError } from '../../lib/emailLayout';
import { capture as captureRef, current as currentRef, clear as clearRef } from '../../lib/referralRef';

// Вход клиента: одно поле и код на почту. Верстка по «Бумаге»
// (docs/redesign/2026-09-04-bumaga/, артборд «Вход · клиенты»).
//
// Регистрации и входа тут не два сценария, а один: нет учётной записи — код её
// заводит, есть — пускает. Именно поэтому клиент больше не может оказаться
// запертым: до 19.08.2026 код существующему адресу не отправлялся, а остальные
// двери закрыты доменным гейтом.
//
// Экран живёт только на клиентском домене, поэтому здесь нет ни «AI Platform»,
// ни «ключа доступа», ни выбора «кто вы» — спрашивать нечего.
// Домен сотрудников берётся из переменной сборки (lib/host.js): бандл ставится
// клиенту, наших адресов в нём быть не должно. Не задан — ссылка не рисуется, а
// текст говорит «на другой вход» без имени домена.
//
// 🪤 Три отступления от макета, все три осознанные:
//  1. Чекбокс согласия остаётся (решение владельца 07.09.2026). Макет заменяет его
//     строкой «Нажимая „Получить код", вы принимаете…» — а согласие с офертой и с
//     обработкой данных модерация ЮKassa сверяет с сайтом, и галочка это то, чем
//     оно доказывается. Мелкой строки из макета здесь нет: два согласия подряд —
//     это не строже, а неряшливее.
//  2. Срок кода — 15 минут, как в `bit-auth/app/signup_service.py:16`. Макет обещал
//     10; правим текст, а не код (решение владельца).
//  3. Подсказки «это корпоративная почта» прямо на вводе нет. Чтобы узнать это до
//     запроса, нужен список наших доменов в бандле, который ставится клиенту, — а
//     его там быть не должно. Ответ приходит от bit-auth, как и раньше.

const CARD = 'w-full max-w-[400px] bg-paper-card rounded-[20px] p-7 flex flex-col gap-4';
const FIELD = 'w-full rounded-control bg-paper-bg px-[14px] py-[13px] text-[15px] '
  + 'text-paper-ink placeholder:text-paper-hint outline-none';
const QUIET = 'text-[12px] text-paper-ink-3 underline underline-offset-[3px] hover:text-paper-ink';

const RESEND_SEC = 30;

const ClientAuthPage = ({ onAuth }) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [staff, setStaff] = useState(false);
  const [busy, setBusy] = useState(false);
  // Две отметки, обе обязательны: учётная запись заводится этим же действием.
  // Юрист (текст чек-бокса, 16.09.2026): согласие на обработку данных не должно
  // подменять акцепт оферты — это два разных документа и два разных действия.
  const [offer, setOffer] = useState(false);
  const [consent, setConsent] = useState(false);
  // Остаток попыток приходит от bit-auth: без него отказ на шестой попытке
  // выглядит как поломка сервиса.
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  // Отсчёт до повторной отправки. Без него человек, у которого письмо не дошло,
  // упирается в экран без выхода: «другая почта» — не то, что ему нужно.
  const [resendIn, setResendIn] = useState(0);
  const timer = useRef(null);

  // Токен ссылки, по которой пришли на клиентский вход (lib/referralRef.js):
  // забирается из адреса сразу, чтобы перезагрузка страницы не «пришла по ссылке» второй раз.
  useEffect(() => { captureRef(); }, []);

  useEffect(() => () => clearInterval(timer.current), []);

  const startResendCountdown = () => {
    clearInterval(timer.current);
    setResendIn(RESEND_SEC);
    timer.current = setInterval(() => {
      setResendIn((n) => {
        if (n <= 1) clearInterval(timer.current);
        return n - 1;
      });
    }, 1000);
  };

  const canonical = () => email.trim().toLowerCase();

  const requestCode = async () => {
    setError('');
    setStaff(false);
    const layout = emailLayoutError(email);
    if (layout) { setError(layout); return; }
    setBusy(true);
    try {
      await authService.signup(canonical());
      setSent(true);
      startResendCountdown();
      logUiEvent('client_code_sent', { component: 'auth' });
    } catch (err) {
      // Корпоративный адрес — не отказ, а «вам на другой вход».
      if (/корпоратив/i.test(err.message)) {
        setStaff(true);
        setError(STAFF_HOST
          ? `Это корпоративный адрес — вход для сотрудников на ${STAFF_HOST}`
          : 'Это корпоративный адрес — вход для сотрудников на другом домене');
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (digits) => {
    setCode(digits);
    if (digits.length !== 6) return;
    setError('');
    setAttemptsLeft(null);
    try {
      const data = await authService.confirmSignup(canonical(), digits, currentRef());
      clearRef();
      await onAuth(data.token);
    } catch (err) {
      setError(err.message);
      setAttemptsLeft(typeof err.attemptsLeft === 'number' ? err.attemptsLeft : null);
    }
  };

  const backToEmail = () => {
    clearInterval(timer.current);
    setResendIn(0);
    setSent(false);
    setCode('');
    setError('');
    setAttemptsLeft(null);
  };

  const renderEmailStep = () => (
    <>
      <div>
        <div className="font-display text-[24px] font-extrabold tracking-[-0.03em] text-paper-ink">
          Вход
        </div>
        <div className="mt-[6px] text-[13px] leading-[1.5] text-paper-ink-2">
          Одно поле — пароль не нужен. Пришлём код на почту.
        </div>
      </div>

      <input
        id="client-email"
        data-testid="client-email"
        type="email"
        placeholder="имя@компания.ru"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={FIELD}
      />

      <label className="flex items-start gap-3 text-[12px] leading-[1.5] text-paper-ink-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid="client-offer"
          checked={offer}
          onChange={(e) => setOffer(e.target.checked)}
          className="mt-[2px] w-4 h-4 accent-paper-accent"
        />
        <span>
          Я принимаю условия{' '}
          <a href="/oferta" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">публичной оферты</a>.
        </span>
      </label>

      <label className="flex items-start gap-3 text-[12px] leading-[1.5] text-paper-ink-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid="client-consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-[2px] w-4 h-4 accent-paper-accent"
        />
        <span>
          Я даю{' '}
          <a href="/consent" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">
            согласие на обработку персональных данных</a>{' '}
          в соответствии с{' '}
          <a href="/privacy" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">
            Политикой конфиденциальности</a>.
        </span>
      </label>

      <button
        type="button"
        data-testid="client-continue"
        disabled={!email || !offer || !consent || busy}
        onClick={requestCode}
        className="rounded-control py-[13px] text-[14px] font-semibold bg-paper-accent text-white hover:bg-paper-accent-hover disabled:bg-paper-bg disabled:text-paper-hint transition-colors"
      >
        {busy ? 'Отправляем…' : 'Получить код'}
      </button>

      {STAFF_URL && (
        <div className="border-t border-paper-bg pt-3 text-[12px] text-paper-ink-3">
          Корпоративная почта?{' '}
          <a href={STAFF_URL} className="text-paper-ink-2 font-semibold hover:text-paper-accent">
            Вход для сотрудников →
          </a>
        </div>
      )}
    </>
  );

  const cells = Array.from({ length: 6 }, (_, i) => ({
    v: code[i] || '',
    active: i === code.length && !error,
  }));

  const renderCodeStep = () => (
    <>
      <div>
        <div className="font-display text-[24px] font-extrabold tracking-[-0.03em] text-paper-ink">
          Код из письма
        </div>
        <div className="mt-[6px] text-[13px] leading-[1.5] text-paper-ink-2">
          Отправили на <b className="font-semibold text-paper-ink">{canonical()}</b>.{' '}
          <button type="button" className="text-[13px] underline underline-offset-[3px]" onClick={backToEmail}>
            Другая почта
          </button>
        </div>
      </div>

      <div className="flex gap-2" aria-hidden="true">
        {cells.map((c, i) => (
          <div
            key={i}
            className={`flex-1 h-14 rounded-control flex items-center justify-center font-mono
                        text-[22px] font-medium text-paper-ink
                        ${error ? 'bg-paper-accent/[0.08]' : 'bg-paper-bg'}
                        ${c.active ? 'outline outline-2 -outline-offset-2 outline-paper-ink' : ''}`}
          >
            {c.v}
          </div>
        ))}
      </div>

      <input
        data-testid="client-code"
        inputMode="numeric"
        maxLength={6}
        autoFocus
        placeholder="Введите 6 цифр"
        value={code}
        onChange={(e) => submitCode(e.target.value.replace(/\D/g, ''))}
        className="w-full rounded-control bg-paper-panel px-[14px] py-[11px] text-[14px] font-mono text-paper-ink placeholder:text-paper-hint outline-none"
      />

      <div className="flex justify-between items-baseline text-[12px] text-paper-ink-3">
        <span>Код живёт 15 минут</span>
        <button
          type="button"
          disabled={resendIn > 0 || busy}
          onClick={requestCode}
          className={resendIn > 0 ? 'text-paper-hint' : QUIET}
        >
          {resendIn > 0 ? `Отправить ещё раз через ${resendIn} с` : 'Отправить ещё раз'}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex items-center justify-center">
      <div className={CARD}>
        {sent ? renderCodeStep() : renderEmailStep()}

        {error && (
          <p data-testid="client-error" className="text-[12px] leading-[1.5] text-paper-accent">
            {error}{attemptsLeft !== null && ` Осталось попыток: ${attemptsLeft}.`}
          </p>
        )}
        {staff && STAFF_URL && (
          <a
            data-testid="client-staff-link"
            href={STAFF_URL}
            className="text-center text-[13px] font-semibold text-paper-accent hover:underline"
          >
            Перейти на {STAFF_HOST}
          </a>
        )}
      </div>
    </div>
  );
};

export default ClientAuthPage;
